import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { JUST_SIGNED_UP_KEY } from '@/hooks/useOnboarding'
import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Button } from '@/components/Button'
import { TextField } from '@/components/Form'
import { OutageBanner } from '@/components/OutageBanner'
import { PasswordRules } from '@/components/PasswordRules'
import { Skeleton } from '@/components/Skeleton'
import { ApiError, api } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { qk } from '@/lib/queryClient'
import { passwordIsValid, validateEmail, validateUsername } from '@/lib/validation'

/**
 * Server-supplied `reason` codes. Each one names what happened and what to do
 * next — a stranger holding a dead link should never have to guess which of the
 * four states they are in.
 */
const REASONS: Record<string, { status: string; title: string; body: string }> = {
  not_found: {
    status: 'Unknown code',
    title: 'That invite link is not valid',
    body: 'Check the link you were sent — it may have been cut short when it was shared.',
  },
  expired: {
    status: 'Expired',
    title: 'This invite has expired',
    body: 'Ask whoever sent it for a fresh link; they can make one in seconds.',
  },
  exhausted: {
    status: 'Used up',
    title: 'This invite has been used up',
    body: 'Every spot on this link is taken. Ask for a new one and you are in.',
  },
  revoked: {
    status: 'Withdrawn',
    title: 'This invite was withdrawn',
    body: 'The person who created it turned it off. Ask them for a new link.',
  },
}

function InviteShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh">
      <OutageBanner />
      <div className="mx-auto flex min-h-[calc(100dvh-3rem)] max-w-sm flex-col justify-center px-4 py-10">
        {children}
      </div>
    </div>
  )
}

/** A single dead-end plate: status flap, one line, one way forward. */
function InviteNotice({
  status,
  title,
  body,
  action,
}: {
  status: string
  title: string
  body: string
  action: ReactNode
}) {
  return (
    <div className="rounded border border-line bg-surface p-5">
      <span className="flap signage inline-block rounded-[3px] px-2 py-1 text-[10px] text-muted">
        {status}
      </span>
      <h1 className="signage-tight mt-4 text-base text-ink">{title}</h1>
      <p className="mt-2 text-sm text-muted">{body}</p>
      <div className="mt-5">{action}</div>
    </div>
  )
}

export function InvitePage() {
  const { code = '' } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState({ username: false, email: false })
  const [formError, setFormError] = useState<string | null>(null)

  const preview = useQuery({
    queryKey: qk.invitePreview(code),
    queryFn: ({ signal }) => api.invites.preview(code, signal),
    retry: false,
    enabled: code.length > 0,
  })

  const redeem = useMutation({
    mutationFn: () =>
      api.invites.redeem(code, {
        username: username.trim(),
        password,
        ...(email.trim() ? { email: email.trim() } : {}),
      }),
    onSuccess: async () => {
      /*
       * Flag the account as brand new so the board can open with a short
       * welcome — where to watch, where to request, which app to install.
       * Someone who has just signed up will not go looking for the guides page
       * on their own, and this is the one moment they are paying attention.
       */
      try {
        localStorage.setItem(JUST_SIGNED_UP_KEY, '1')
      } catch {
        // Private mode: they simply get the normal onboarding instead.
      }

      // The redeem call signs the new user in, so refresh auth before landing.
      await queryClient.invalidateQueries({ queryKey: qk.me })
      navigate('/', { replace: true })
    },
    onError: (error) =>
      setFormError(
        error instanceof ApiError ? error.message : 'Could not create your account. Try again.',
      ),
  })

  if (preview.isPending) {
    return (
      <InviteShell>
        <div className="space-y-4 rounded border border-line bg-surface p-5">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      </InviteShell>
    )
  }

  if (preview.isError) {
    return (
      <InviteShell>
        <InviteNotice
          status="No answer"
          title="We could not check that invite"
          body="The portal could not reach the server just now. Your link is probably fine — try again in a moment."
          action={
            <Button variant="primary" className="w-full" onClick={() => void preview.refetch()}>
              Try again
            </Button>
          }
        />
      </InviteShell>
    )
  }

  const data = preview.data
  if (!data.valid) {
    const copy = REASONS[data.reason ?? 'not_found'] ?? REASONS.not_found
    return (
      <InviteShell>
        <InviteNotice
          status={copy?.status ?? 'Not valid'}
          title={copy?.title ?? 'That invite link is not valid'}
          body={copy?.body ?? 'Ask whoever sent it for a fresh link.'}
          action={
            <Link to="/login" className="signage text-[10px] text-accent hover:underline">
              Already have an account? Sign in
            </Link>
          }
        />
      </InviteShell>
    )
  }

  const usernameError = touched.username ? validateUsername(username) : null
  const emailError = touched.email ? validateEmail(email) : null
  const canSubmit =
    !validateUsername(username) && passwordIsValid(password) && !validateEmail(email)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)
    setTouched({ username: true, email: true })
    if (!canSubmit) return
    redeem.mutate()
  }

  return (
    <InviteShell>
      <header className="mb-5">
        <p className="signage text-[9px] text-faint">You are invited to</p>
        <h1 className="signage mt-1.5 text-lg text-ink sm:text-xl">{data.serverName}</h1>
        <div aria-hidden className="rule-signal mt-3 h-px w-full" />
        <p className="mt-3 text-sm text-muted">
          Pick a username and password. The same login works for the media server and this portal.
        </p>

        {data.profileName || data.expiresAt ? (
          <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            {data.profileName ? (
              <div>
                <dt className="signage text-[9px] text-faint">Access</dt>
                <dd className="mt-0.5 font-mono text-xs text-ink">{data.profileName}</dd>
              </div>
            ) : null}
            {data.expiresAt ? (
              <div>
                <dt className="signage text-[9px] text-faint">Link expires</dt>
                <dd className="mt-0.5 font-mono text-xs text-ink">{formatDate(data.expiresAt)}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </header>

      <form onSubmit={onSubmit} className="space-y-4 rounded border border-line bg-surface p-4">
        <TextField
          label="Username"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          required
          value={username}
          error={usernameError}
          onChange={(event) => {
            setTouched((state) => ({ ...state, username: true }))
            setUsername(event.target.value)
          }}
        />

        <div className="space-y-2">
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <PasswordRules value={password} />
        </div>

        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="Optional"
          value={email}
          error={emailError}
          hint="Only used for account notices."
          onChange={(event) => {
            setTouched((state) => ({ ...state, email: true }))
            setEmail(event.target.value)
          }}
        />

        {formError ? (
          <p
            role="alert"
            className="rounded-[3px] border border-danger/40 bg-danger-dim px-3 py-2 text-sm text-ink"
          >
            {formError}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={redeem.isPending}
          disabled={!canSubmit}
        >
          Create my account
        </Button>
      </form>

      <p className="mt-5 text-center text-xs text-muted">
        Already have an account?{' '}
        <Link to="/login" className="text-accent hover:underline">
          Sign in instead
        </Link>
      </p>
    </InviteShell>
  )
}
